import { Injectable, isDevMode } from '@angular/core';
import gql from 'graphql-tag';
import { Apollo, QueryRef } from 'apollo-angular';
import { event } from '@app/__generated__/event';
import { createEvent } from '@app/__generated__/createEvent';
import { updateEvent } from 'app/__generated__/updateEvent';
import { RoutingService } from '@app/routing.service';
import {
  AppendEventGQL,
  CreateEventGQL,
  CreateEventInput,
  EventGQL,
  EventQuery,
  EventQueryVariables,
  OnEventAppendedGQL,
  UpdateEventGQL
} from '@app/graphql/generated';
import { Observable, of, ReplaySubject, Subject } from 'rxjs';
import { filter, map, startWith, take, tap } from 'rxjs/operators';
import { Socket } from 'ngx-socket-io';
import { UserService } from '@app/user/user.service';
import { EventModel } from '@app/event/event.model';
import { NavigationEnd, Router } from '@angular/router';
import { config } from '@app/app.config';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '@app/auth/auth.service';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { SettingKey } from '@app/admin/users-admin/edit-user/edit-user.component';

export interface StreamSettings {
  font: string;
  fontSize: number;
  theme: 'dark' | 'light';
  language: string;
  uppercase: boolean;
  background: string;
  textColor: string;
}

export interface EventState {
  id: number;
  title: string;
  canEdit: boolean;
  completed: boolean;
  language: string;
  service: string;
  online: boolean;
  ts: number;
}

const themeDarkColor = '#0f0e0e';
const themeLightColor = '#ffffff';

const defaultSettings: StreamSettings = {
  font: 'Arial',
  fontSize: 28,
  theme: 'dark',
  language: null,
  uppercase: false,
  background: themeDarkColor,
  textColor: themeLightColor
};

@Injectable({
  providedIn: 'root'
})
export class EventService {
  public isStreaming = false;
  public isSplitScreen$ = new ReplaySubject<boolean>(1);
  public isFullScreen$ = new ReplaySubject<boolean>(1);
  public settings1$ = new ReplaySubject<StreamSettings>(1);
  public settings2$ = new ReplaySubject<StreamSettings>(1);
  private queryRef: QueryRef<EventQuery, EventQueryVariables>;
  private queryRefByEventWindow: {
    [key: string]: QueryRef<EventQuery, EventQueryVariables>;
  } = {};
  private unsubscribeToMore: any = {};
  private currentUser: any;

  public isEventPage$ = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    tap(event => console.log(event)),
    // @ts-ignore
    map(event => event.url.includes('/events/')),
  );
  public resubscribeHandler: any;
  public unsubscribeHandler: any;

  constructor(
    private apollo: Apollo,
    private routingService: RoutingService,
    public router: Router,
    private eventGql: EventGQL,
    private onEventAppendedGQL: OnEventAppendedGQL,
    private appendEventGQL: AppendEventGQL,
    private updateEventGQL: UpdateEventGQL,
    private createEventGQL: CreateEventGQL,
    private socket: Socket,
    private userService: UserService,
    private http: HttpClient,
    private authService: AuthService
  ) {
    this.settings1$.next(defaultSettings);
    this.settings2$.next(defaultSettings);
    this.isFullScreen$.next(true);
    this.isEventPage$.subscribe(isEventPage => {
      console.log('isEventPage', isEventPage);
      
      if (!isEventPage) {
        this.isFullScreen$.next(false);
      } else {
        this.userService
          .currentUser()
          .pipe(
            tap((currentUser: any) => {
              const fullScreenSetting = currentUser.settings.find(s => s.key === SettingKey.full_screen);
              if (fullScreenSetting)
                this.isFullScreen$.next(fullScreenSetting.value === 'true');
              else
                this.isFullScreen$.next(true);
            })
          )
          .subscribe();
      }
    });
  }

  public listEvents() {
  }

  public getEventStatus(eventId: string) : Observable<EventState> {
    return this.http.get<EventState>(config.httpURL + `/api/event/${eventId}/status?ts=${Date.now()}`,
      { headers: { Authorization: `Bearer ${this.authService.getAccessToken()}` }}
    );
  }

  public appendText(eventId: string, words) {
    const variables = {
      input: {
        shortId: eventId,
        words: words
      }
    };

    this.socket.emit('appendText', {
      shortId: eventId,
      words: words,
      userId: this.userService._currentUser.id
    });

    return of(true);

    // return this.appendEventGQL.mutate(variables);
  }

  public createEvent(event: EventModel) {
    const eventDto = this.serializeEvent(event);

    const variables = { input: eventDto };

    return this.createEventGQL.mutate(variables);
  }

  public updateEvent(shortId, event: CreateEventInput | any) {
    // const eventDto = this.serializeEvent(event);

    const variables = { input: { shortId, ...event } };
    return this.updateEventGQL.mutate(variables);
  }

  public completeEvent() {
    const id = this.routingService.eventId;
    return this.updateEvent(id, { completed: true });
  }

  privateIsStreaming() {
  }

  enterSplitScreen() {
    this.isSplitScreen$.next(true);
  }

  exitSplitScreen() {
    this.isSplitScreen$.next(false);
  }

  enterFullscreen() {
    this.isFullScreen$.next(true);
  }

  exitFullscreen() {
    this.isFullScreen$.next(false);
  }

  setTheme(theme: any, options: { window: number }) {
    const settings$ = this.getSettings$(options.window);
    settings$
      .pipe(
        take(1),
        tap(settings => {
          settings$.next({
            ...settings,
            theme: theme,
            background: theme === 'light' ? themeLightColor : themeDarkColor,
            textColor: theme === 'light' ? themeDarkColor : themeLightColor
          });
        })
      )
      .subscribe();
  }

  setUppercase(eventId: string, uppercase: boolean, options: { window: number }) {
    const settings$ = this.getSettings$(options.window);
    settings$
      .pipe(
        take(1),
        tap(settings => {
          settings$.next({ ...settings, uppercase });
        })
      )
      .subscribe();
  }

  async setLanguage(eventId: string, language: any, options: { window: number }) {
    const settings$ = this.getSettings$(options.window);
    settings$
      .pipe(
        take(1),
        tap(settings => {
          settings$.next({ ...settings, language });
        })
      )
      .subscribe();
    
    if (this.resubscribeHandler)
      await this.resubscribeHandler();
  }

  setFont(font: string, options: { window: number }) {
    const settings$ = this.getSettings$(options.window);
    settings$
      .pipe(
        take(1),
        tap(settings => {
          settings$.next({ ...settings, font: font });
        })
      )
      .subscribe();
  }

  setFontSize(size: number, options: { window: number }) {
    const settings$ = this.getSettings$(options.window);
    settings$
      .pipe(
        take(1),
        tap(settings => {
          settings$.next({ ...settings, fontSize: size });
        })
      )
      .subscribe();
  }

  setBackground(background: any, options: { window: number }) {
    const settings$ = this.getSettings$(options.window);
    settings$
      .pipe(
        take(1),
        tap(settings => {
          settings$.next({ ...settings, background });
        })
      )
      .subscribe();
  }

  setTextColor(color: any, options: { window: number }) {
    const settings$ = this.getSettings$(options.window);
    settings$
      .pipe(
        take(1),
        tap(settings => {
          settings$.next({ ...settings, textColor: color });
        })
      )
      .subscribe();
  }

  getQueryRef(eventId, window) {
    return this.queryRefByEventWindow[`e_${eventId}_${window}`];
  }

  getSettings$(window): ReplaySubject<StreamSettings> {
    return window === 1 ? this.settings1$ : this.settings2$;
  }

  reset() {
    this.queryRefByEventWindow = {};
    this.isSplitScreen$.next(false);
  }

  private serializeEvent(event: EventModel) {
    const serviceEngineMap = {
      engine1: 'webkit',
      engine2: 'google',
      engine3: 'watson',
      engine5: 'deepgram',
      engine6: 'transcribe'
    };

    const service = serviceEngineMap[event.engine];

    const eventDto = { ...event, service };
    delete eventDto.engine;
    return eventDto;
  }

  downloadAudio(eventId: string, name: string) {
    const link = document.createElement('a');
    document.body.appendChild(link); // Firefox compatibility
    link.download = `${name}.wav`; // TODO: this is no longer supported, need to figure this out
    link.href = `${config.httpURL}/api/event/${eventId}/downloadAudio`;
    link.click();
    document.body.removeChild(link);
    document.location.href = `${config.httpURL}/api/event/${eventId}/downloadAudio`;
  }

  deleteAudio(eventId: string) {
    return this.http
      .delete(config.httpURL + `/api/event/${eventId}/deleteAudio`,
        { headers: { Authorization: `Bearer ${this.authService.getAccessToken()}` }});
  }

  downloadSubtitles(eventId: string, type: string, language: string, timing: number, lineLength: number) {
    document.location.href = `${config.httpURL}/api/event/${eventId}/downloadSubtitles/${type}?timing=${timing}&lang=${language}&lineLength=${lineLength}`;
  }

  disableEncoders(eventId: string, complete: boolean = false) {
    if (complete) {
      return this.http
      .post(config.httpURL + `/api/event/${eventId}/encoders/disable?complete=true`, undefined,
        { headers: { Authorization: `Bearer ${this.authService.getAccessToken()}` }});
    } else {      
      return this.http
      .post(config.httpURL + `/api/event/${eventId}/encoders/disable`, undefined,
        { headers: { Authorization: `Bearer ${this.authService.getAccessToken()}` }});
    }
  }

  enableEncoders(eventId: string) {
    return this.http
      .post(config.httpURL + `/api/event/${eventId}/encoders/enable`, undefined,
        { headers: { Authorization: `Bearer ${this.authService.getAccessToken()}` }});
  }
}
